import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { API_URL, formatDate } from "../../lib/utils";
import {
    Shield,
    Loader2,
    Eye,
    RefreshCw,
    Search,
    Filter,
    Activity,
    Building2,
    Users,
    FileText,
    Calendar,
    Globe,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

const ACTION_LABELS = {
    tenant_created: "Création de tenant",
    tenant_updated: "Modification de tenant",
    tenant_activated: "Activation de tenant",
    tenant_deactivated: "Désactivation de tenant",
    tenant_plan_changed: "Changement de plan",
    tenant_currency_changed: "Changement de devise",
    user_created: "Création d'utilisateur",
    user_invited: "Invitation d'utilisateur",
    user_deactivated: "Désactivation d'utilisateur",
    plan_updated: "Modification de plan",
    subscription_created: "Création d'abonnement",
    subscription_cancelled: "Annulation d'abonnement",
};

const RESOURCE_ICONS = {
    tenant: Building2,
    user: Users,
    plan: FileText,
    subscription: Calendar,
};

export const AdminAuditPage = () => {
    const { token } = useAuth();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingStats, setLoadingStats] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [actionFilter, setActionFilter] = useState("all");
    const [detailDialog, setDetailDialog] = useState(null);
    const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

    const fetchLogs = useCallback(async (offset = 0) => {
        setLoading(true);
        try {
            let url = `${API_URL}/platform/audit-logs?limit=50&offset=${offset}`;
            if (actionFilter && actionFilter !== "all") {
                url += `&action=${actionFilter}`;
            }
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch audit logs");
            const data = await res.json();
            setLogs(data.logs);
            setPagination({ total: data.total, limit: data.limit, offset: data.offset });
        } catch (error) {
            toast.error("Erreur lors du chargement des logs");
        } finally {
            setLoading(false);
        }
    }, [token, actionFilter]);

    const fetchStats = useCallback(async () => {
        setLoadingStats(true);
        try {
            const res = await fetch(`${API_URL}/platform/audit-logs/stats`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch stats");
            const data = await res.json();
            setStats(data);
        } catch (error) {
            console.error("Error fetching audit stats:", error);
        } finally {
            setLoadingStats(false);
        }
    }, [token]);

    useEffect(() => {
        fetchLogs();
        fetchStats();
    }, [fetchLogs, fetchStats]);

    const filteredLogs = logs.filter((log) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            log.actor_email?.toLowerCase().includes(q) ||
            log.resource_name?.toLowerCase().includes(q) ||
            log.action_label?.toLowerCase().includes(q)
        );
    });

    const getActionBadgeColor = (action) => {
        if (action.includes("created")) return "bg-green-100 text-green-800";
        if (action.includes("deleted") || action.includes("deactivated")) return "bg-red-100 text-red-800";
        if (action.includes("updated") || action.includes("changed")) return "bg-blue-100 text-blue-800";
        return "bg-gray-100 text-gray-800";
    };

    const ResourceIcon = ({ type }) => {
        const Icon = RESOURCE_ICONS[type] || Globe;
        return <Icon className="w-4 h-4" />;
    };

    return (
        <div className="space-y-6" data-testid="admin-audit-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
                        <Shield className="w-6 h-6 text-primary" />
                        Journal d'audit
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Traçabilité complète des actions administrateur
                    </p>
                </div>
                <Button onClick={() => { fetchLogs(); fetchStats(); }} variant="outline" data-testid="refresh-audit-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total des logs</p>
                                <p className="text-2xl font-bold">
                                    {loadingStats ? "..." : stats?.total_logs?.toLocaleString() || 0}
                                </p>
                            </div>
                            <Activity className="w-8 h-8 text-muted-foreground" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Aujourd'hui</p>
                                <p className="text-2xl font-bold text-green-600">
                                    {loadingStats ? "..." : stats?.today_logs || 0}
                                </p>
                            </div>
                            <Calendar className="w-8 h-8 text-green-600" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Cette semaine</p>
                                <p className="text-2xl font-bold text-blue-600">
                                    {loadingStats ? "..." : stats?.week_logs || 0}
                                </p>
                            </div>
                            <Activity className="w-8 h-8 text-blue-600" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Types d'actions</p>
                                <p className="text-2xl font-bold">
                                    {loadingStats ? "..." : stats?.action_breakdown?.length || 0}
                                </p>
                            </div>
                            <Filter className="w-8 h-8 text-muted-foreground" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts */}
            {stats && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Daily Activity */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Activité des 7 derniers jours</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={stats.daily_activity}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>

                    {/* Action Breakdown */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Répartition par action</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={stats.action_breakdown}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={70}
                                        dataKey="count"
                                        nameKey="action"
                                        label={({ action, percent }) =>
                                            `${ACTION_LABELS[action] || action} (${(percent * 100).toFixed(0)}%)`
                                        }
                                        labelLine={false}
                                    >
                                        {stats.action_breakdown.map((entry, index) => (
                                            <Cell key={entry.action} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(value, name) => [value, ACTION_LABELS[name] || name]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Logs Table */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>Historique des actions</CardTitle>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 w-64"
                                    data-testid="audit-search-input"
                                />
                            </div>
                            <Select value={actionFilter} onValueChange={setActionFilter}>
                                <SelectTrigger className="w-48" data-testid="audit-action-filter">
                                    <SelectValue placeholder="Filtrer par action" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes les actions</SelectItem>
                                    {Object.entries(ACTION_LABELS).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : filteredLogs.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            Aucun log d'audit trouvé
                        </div>
                    ) : (
                        <>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Acteur</TableHead>
                                        <TableHead>Action</TableHead>
                                        <TableHead>Ressource</TableHead>
                                        <TableHead>IP</TableHead>
                                        <TableHead className="w-[80px]">Détails</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredLogs.map((log) => (
                                        <TableRow key={log.audit_id} data-testid={`audit-row-${log.audit_id}`}>
                                            <TableCell className="text-sm">
                                                {formatDate(log.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-medium">{log.actor_email}</span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={getActionBadgeColor(log.action)}>
                                                    {log.action_label || log.action}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <ResourceIcon type={log.resource_type} />
                                                    <span>{log.resource_name || log.resource_id || "-"}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {log.ip_address || "-"}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => setDetailDialog(log)}
                                                    data-testid={`view-audit-${log.audit_id}`}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {/* Pagination */}
                            <div className="flex items-center justify-between mt-4">
                                <p className="text-sm text-muted-foreground">
                                    Affichage {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} sur {pagination.total}
                                </p>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={pagination.offset === 0}
                                        onClick={() => fetchLogs(Math.max(0, pagination.offset - pagination.limit))}
                                    >
                                        Précédent
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={pagination.offset + pagination.limit >= pagination.total}
                                        onClick={() => fetchLogs(pagination.offset + pagination.limit)}
                                    >
                                        Suivant
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Détails de l'action</DialogTitle>
                    </DialogHeader>
                    {detailDialog && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Date</p>
                                    <p className="font-medium">{formatDate(detailDialog.created_at)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Acteur</p>
                                    <p className="font-medium">{detailDialog.actor_email}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Action</p>
                                    <Badge className={getActionBadgeColor(detailDialog.action)}>
                                        {detailDialog.action_label}
                                    </Badge>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Ressource</p>
                                    <p className="font-medium">{detailDialog.resource_type} / {detailDialog.resource_name || detailDialog.resource_id}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Adresse IP</p>
                                    <p className="font-medium">{detailDialog.ip_address || "N/A"}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">ID Audit</p>
                                    <p className="font-mono text-xs">{detailDialog.audit_id}</p>
                                </div>
                            </div>
                            
                            {detailDialog.details && Object.keys(detailDialog.details).length > 0 && (
                                <div>
                                    <p className="text-sm text-muted-foreground mb-2">Modifications</p>
                                    <div className="bg-muted rounded-lg p-4 font-mono text-sm overflow-auto max-h-60">
                                        <pre>{JSON.stringify(detailDialog.details, null, 2)}</pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminAuditPage;
